import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle, Clapperboard, ExternalLink, Film, Loader2, Plus,
  RefreshCw, Sparkles, Trash2, Users,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ImageLightbox } from './ImageLightbox';

// Bank ujęć wideo: powtarzalne klipy b-roll per produkt, image-first:
// kadr gpt-image (postać + packshot jako referencje) -> akceptacja usera
// ("Animuj") -> animacja Kie.ai. Gotowe klipy reużywane w produkcji reklam
// (tryby: bank / bank + dogeneruj / od zera).

interface BankCategory {
  key: string;
  label: string;
  description: string;
}

interface BankClip {
  id: string;
  category: string;
  name: string;
  imagePrompt: string;
  motionPrompt: string | null;
  actorAvatarId: string | null;
  driveFileUrl: string | null;
  status: string;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AvatarOption {
  id: string;
  name: string;
  status: string;
  previewUrl: string | null;
}

const POLL_INTERVAL_MS = 8000;

function clipStatusBadge(status: string, error?: string | null) {
  if (['PENDING', 'GENERATING'].includes(status)) {
    return <span className="flex items-center gap-1 text-[10px] font-medium text-warning"><Loader2 className="w-3 h-3 animate-spin" /> Kadr w generacji...</span>;
  }
  if (status === 'IMAGE_READY') {
    return <span className="flex items-center gap-1 text-[10px] font-medium text-accent-info"><CheckCircle className="w-3 h-3" /> Kadr gotowy - zaakceptuj</span>;
  }
  if (['VIDEO_QUEUED', 'VIDEO_PROCESSING'].includes(status)) {
    return <span className="flex items-center gap-1 text-[10px] font-medium text-warning"><Loader2 className="w-3 h-3 animate-spin" /> Animacja...</span>;
  }
  if (status === 'FAILED') {
    return <span className="flex items-center gap-1 text-[10px] font-medium text-red-400" title={error || undefined}><AlertCircle className="w-3 h-3" /> Błąd</span>;
  }
  return <span className="flex items-center gap-1 text-[10px] font-medium text-success"><CheckCircle className="w-3 h-3" /> Klip gotowy</span>;
}

export function VideoBankPanel({ productId }: { productId: string }) {
  const [clips, setClips] = useState<BankClip[]>([]);
  const [categories, setCategories] = useState<BankCategory[]>([]);
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [actorAvatarId, setActorAvatarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [seriesStarting, setSeriesStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BankClip | null>(null);
  // Lupka na kadr + popup regeneracji z uwagami operatora
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [retryTarget, setRetryTarget] = useState<BankClip | null>(null);
  const [retryNote, setRetryNote] = useState('');
  const [retryStarting, setRetryStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await labApi.getVideoBank(productId);
      setClips(res.data.data?.clips || []);
      setCategories(res.data.data?.categories || []);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się pobrać Banku ujęć.');
    }
  }, [productId]);

  useEffect(() => {
    void load().finally(() => setLoading(false));
    labApi.getAvatars()
      .then(res => {
        const ready = (res.data.data || []).filter((avatar: any) => avatar.status === 'READY');
        setAvatars(ready.map((avatar: any) => ({ id: avatar.id, name: avatar.name, status: avatar.status, previewUrl: avatar.previewUrl || null })));
        if (ready.length > 0) setActorAvatarId(ready[0].id);
      })
      .catch(() => setAvatars([]));
  }, [load]);

  // Escape zamyka popup regeneracji (konwencja projektu)
  useEffect(() => {
    if (!retryTarget) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRetryTarget(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [retryTarget]);

  const hasPending = clips.some(clip => ['PENDING', 'GENERATING', 'VIDEO_QUEUED', 'VIDEO_PROCESSING'].includes(clip.status));
  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, load]);

  const handleSeries = async () => {
    setSeriesStarting(true);
    setError(null);
    try {
      await labApi.createVideoBankSeries(productId, { actorAvatarId: actorAvatarId || null });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się wystartować serii.');
    } finally {
      setSeriesStarting(false);
    }
  };

  const handleAddClip = async (category: string) => {
    setError(null);
    try {
      await labApi.createVideoBankClip(productId, { category, actorAvatarId: actorAvatarId || null });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się dodać klipu.');
    }
  };

  const action = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Operacja nie powiodła się.');
    }
  };

  const clipsByCategory = new Map<string, BankClip[]>();
  for (const clip of clips) {
    const list = clipsByCategory.get(clip.category) || [];
    list.push(clip);
    clipsByCategory.set(clip.category, list);
  }
  const customClips = clipsByCategory.get('CUSTOM') || [];
  const readyCount = clips.filter(clip => clip.status === 'READY').length;

  const renderClipCard = (clip: BankClip) => (
    <div key={clip.id} className="rounded-lg border border-border-subtle overflow-hidden bg-surface-hover/30 group relative">
      <div className="aspect-[3/4] bg-surface-hover">
        {['IMAGE_READY', 'VIDEO_QUEUED', 'VIDEO_PROCESSING', 'READY'].includes(clip.status) ? (
          <button
            type="button"
            onClick={() => setLightbox({ src: labApi.getVideoBankImageUrl(productId, clip.id, clip.updatedAt), alt: clip.name })}
            className="w-full h-full cursor-zoom-in"
            title="Powiększ kadr"
            aria-label={`Powiększ kadr: ${clip.name}`}
          >
            <img
              src={labApi.getVideoBankImageUrl(productId, clip.id, clip.updatedAt)}
              alt={clip.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </button>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {['PENDING', 'GENERATING'].includes(clip.status)
              ? <Loader2 className="w-5 h-5 animate-spin text-warning" />
              : <Film className="w-5 h-5 text-text-disabled" />}
          </div>
        )}
      </div>
      <div className="px-2 py-1.5 space-y-1">
        <span className="text-[10px] font-medium text-text-secondary block truncate">{clip.name}</span>
        {clipStatusBadge(clip.status, clip.error)}
        {clip.status === 'FAILED' && clip.error && (
          <span className="text-[9px] text-red-400 block truncate" title={clip.error}>{clip.error}</span>
        )}
        <div className="flex items-center gap-1 flex-wrap pt-0.5">
          {clip.status === 'IMAGE_READY' && (
            <button
              onClick={() => void action(() => labApi.animateVideoBankClip(productId, clip.id))}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-accent-primary rounded-md hover:bg-accent-primary/90 transition-all"
            >
              <Clapperboard className="w-3 h-3" /> Animuj
            </button>
          )}
          {clip.status === 'READY' && clip.driveFileUrl && (
            <a
              href={clip.driveFileUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-md hover:bg-accent-primary/15 transition-all"
            >
              <ExternalLink className="w-3 h-3" /> Klip
            </a>
          )}
          {!['PENDING', 'GENERATING', 'VIDEO_QUEUED', 'VIDEO_PROCESSING'].includes(clip.status) && (
            <button
              onClick={() => { setRetryTarget(clip); setRetryNote(''); }}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-text-secondary bg-surface-hover/40 border border-border rounded-md hover:text-text-primary hover:border-border-strong transition-all"
              title="Wygeneruj kadr od nowa (z uwagami)"
            >
              <RefreshCw className="w-3 h-3" /> Kadr
            </button>
          )}
          <button
            onClick={() => setDeleteTarget(clip)}
            className="p-1 text-text-muted hover:text-red-400 transition-colors ml-auto"
            title="Usuń klip"
            aria-label="Usuń klip"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Ładowanie Banku ujęć...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-text-primary">Bank wideo</h2>
          <p className="text-sm text-text-secondary mt-1">
            Biblioteka powtarzalnych ujęć produktu do reklam wideo. Gotowe klipy: {readyCount}.
          </p>
        </div>
        <button
          onClick={handleSeries}
          disabled={seriesStarting}
          className="flex items-center gap-2 px-4 py-2.5 bg-accent-primary/15 border border-accent-primary/30 rounded-lg text-accent-primary text-sm font-medium hover:bg-accent-primary/25 transition-colors disabled:opacity-40 shrink-0 self-start"
        >
          {seriesStarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generuj serię startową
        </button>
      </div>

      {/* Wybór postaci: miniatury zamiast selecta (nazwy avatarów bywają nieczytelne) */}
      {avatars.length > 0 && (
        <div>
          <span className="text-xs font-medium text-text-secondary mb-1.5 block">Postać w ujęciach</span>
          <div className="flex items-center gap-2 flex-wrap">
            {avatars.map(avatar => (
              <button
                key={avatar.id}
                type="button"
                onClick={() => setActorAvatarId(avatar.id)}
                className={`flex items-center gap-2 pr-3 rounded-lg border transition-all overflow-hidden ${actorAvatarId === avatar.id ? 'border-accent-primary/50 bg-accent-primary/10' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                title={avatar.name}
              >
                <span className="w-9 h-9 bg-surface-hover shrink-0">
                  {avatar.previewUrl ? (
                    <img src={avatar.previewUrl} alt={avatar.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center"><Users className="w-4 h-4 text-text-disabled" /></span>
                  )}
                </span>
                <span className={`text-[11px] font-medium truncate max-w-28 ${actorAvatarId === avatar.id ? 'text-accent-primary' : 'text-text-primary'}`}>{avatar.name}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setActorAvatarId('')}
              className={`px-3 py-2 rounded-lg border text-[11px] font-medium transition-all ${actorAvatarId === '' ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary' : 'border-border bg-surface-hover/30 text-text-secondary hover:border-border-strong'}`}
            >
              Bez postaci
            </button>
          </div>
        </div>
      )}

      {avatars.length === 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-xs text-warning">
          <Users className="w-4 h-4 shrink-0 mt-0.5" />
          Brak gotowej postaci w Avatarach UGC - klipy z osobą będą generowane z osobą generyczną. Stwórz postać, żeby bank był spójny z reklamami.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-sm text-danger">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {clips.length === 0 ? (
        <EmptyState
          icon={Film}
          title="Bank wideo jest pusty"
          description="Wygeneruj serię startową - komplet powtarzalnych ujęć produktu, gotowych do użycia w każdej reklamie."
          buttonLabel="Generuj serię startową"
          onButtonClick={() => void handleSeries()}
          buttonIcon={Sparkles}
        />
      ) : (
        <div className="space-y-5">
          {categories.map(category => {
            const categoryClips = clipsByCategory.get(category.key) || [];
            return (
              <div key={category.key}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">{category.label}</h3>
                  <span className="text-[10px] text-text-disabled">{category.description}</span>
                  <button
                    onClick={() => void handleAddClip(category.key)}
                    className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-accent-info bg-info/10 border border-accent-info/20 rounded-md hover:bg-info/15 transition-all ml-auto"
                    title="Dodaj alternatywny klip w tej kategorii"
                  >
                    <Plus className="w-3 h-3" /> Wariant
                  </button>
                </div>
                {categoryClips.length === 0 ? (
                  <p className="text-[11px] text-text-disabled">Brak klipów - dodaj wariant albo wygeneruj serię.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {categoryClips.map(renderClipCard)}
                  </div>
                )}
              </div>
            );
          })}

          {customClips.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Dogenerowane (z kampanii)</h3>
                <span className="text-[10px] text-text-disabled">unikatowe ujęcia z trybu hybrydowego - trafiają tu automatycznie</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {customClips.map(renderClipCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Popup regeneracji kadru z uwagami operatora */}
      {retryTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setRetryTarget(null)} />
          <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-md p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-text-primary mb-1">Regeneruj kadr</h3>
            <p className="text-xs text-text-muted mb-4">{retryTarget.name} - opisz co poprawić, zamiast losować w nieskończoność.</p>
            <label htmlFor="bank-retry-note" className="text-xs font-medium text-text-secondary mb-1.5 block">Uwagi (opcjonalnie)</label>
            <textarea
              id="bank-retry-note"
              value={retryNote}
              onChange={e => setRetryNote(e.target.value)}
              placeholder="np. produkt bliżej kamery, etykieta ma być czytelna, zmień tło na kuchnię..."
              rows={3}
              autoFocus
              className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
            />
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={async () => {
                  setRetryStarting(true);
                  await action(() => labApi.retryVideoBankImage(productId, retryTarget.id, retryNote.trim() || undefined));
                  setRetryStarting(false);
                  setRetryTarget(null);
                }}
                disabled={retryStarting}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
              >
                {retryStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Regeneruj kadr
              </button>
              <button
                onClick={() => setRetryTarget(null)}
                className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Usunąć klip z banku?"
        message={deleteTarget ? <>Czy na pewno chcesz usunąć <strong>{deleteTarget.name}</strong>? Plik na Google Drive zostaje.</> : ''}
        confirmLabel="Usuń klip"
        danger
        onConfirm={async () => {
          if (!deleteTarget) return;
          await action(() => labApi.deleteVideoBankClip(productId, deleteTarget.id));
          setDeleteTarget(null);
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
